import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Divider,
  Chip,
  useTheme,
  Button,
  Tooltip,
  IconButton,
  Stack,
  Avatar,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Payments as PaymentsIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

// Componente estilizado para o progresso linear
const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
  }
}));

// Interface para o histórico de planos
interface PlanHistoryItem {
  date: string;
  plan: string;
  action: 'upgrade' | 'downgrade' | 'initial' | 'renewal';
  color: string;
}

// Componente para exibir o uso atual e histórico de planos
const UsageHistory: React.FC = () => {
  const theme = useTheme();
  const userData = useSelector((state: RootState) => state.user.data);
  const leads = useSelector((state: RootState) => state.leads.items);
  const products = useSelector((state: RootState) => state.products.items);
  
  // Calcular limites com base no plano atual
  const getLimit = (type: 'leads' | 'products'): number => {
    if (userData.plan === 'Pro') return Infinity;
    if (userData.plan === 'Basic') return type === 'leads' ? 200 : 50;
    return type === 'leads' ? 50 : 20;
  };
  
  // Calcular percentual de uso
  const calculateUsage = (current: number, limit: number): number => {
    if (limit === Infinity) return 0; // Para planos ilimitados
    return Math.min((current / limit) * 100, 100);
  };
  
  // Dados de uso
  const leadsLimit = getLimit('leads');
  const productsLimit = getLimit('products');
  const leadsUsage = calculateUsage(leads.length, leadsLimit);
  const productsUsage = calculateUsage(products.length, productsLimit);
  
  // Dados para o gráfico de pizza
  const whatsappData = [
    { name: 'Usado', value: 65, color: theme.palette.primary.main },
    { name: 'Disponível', value: 35, color: theme.palette.grey[300] },
  ];
  
  const emailData = [
    { name: 'Usado', value: 42, color: theme.palette.secondary.main },
    { name: 'Disponível', value: 58, color: theme.palette.grey[300] },
  ];
  
  // Histórico de planos (simulado)
  const planHistory: PlanHistoryItem[] = [
    {
      date: '10/01/2025',
      plan: 'Free (Trial)',
      action: 'initial',
      color: theme.palette.grey[500],
    },
    {
      date: '25/01/2025',
      plan: 'Intermediário',
      action: 'upgrade',
      color: theme.palette.primary.main,
    },
    {
      date: '25/02/2025',
      plan: 'Premium',
      action: 'upgrade',
      color: theme.palette.secondary.main,
    },
    {
      date: '25/03/2025',
      plan: 'Premium',
      action: 'renewal',
      color: theme.palette.secondary.main,
    },
  ];
  
  // Renderizar ícone de ação
  const renderActionIcon = (action: string) => {
    switch (action) {
      case 'upgrade':
        return <ArrowUpwardIcon fontSize="small" />;
      case 'downgrade':
        return <ArrowDownwardIcon fontSize="small" />;
      case 'renewal':
        return <PaymentsIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };
  
  // Renderizar texto de ação
  const renderActionText = (action: string) => {
    switch (action) {
      case 'upgrade':
        return 'Upgrade';
      case 'downgrade':
        return 'Downgrade';
      case 'renewal':
        return 'Renovação';
      default:
        return 'Ativação';
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: '16px', 
        mb: 4,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative',
        p: 3
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '5px', 
        background: 'linear-gradient(90deg, #3f51b5, #f50057)'
      }} />
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TrendingUpIcon sx={{ color: theme.palette.primary.main, mr: 1.5, fontSize: '2rem' }} />
        <Typography variant="h5" fontWeight="bold">
          Uso Atual e Histórico
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Utilização do Plano Atual
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      Leads
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {leads.length} / {leadsLimit === Infinity ? '∞' : leadsLimit}
                    </Typography>
                  </Box>
                  <StyledLinearProgress 
                    variant="determinate" 
                    value={leadsUsage} 
                    color="primary" 
                  />
                  {leadsUsage > 80 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: '0.9rem', mr: 0.5 }} />
                      <Typography variant="caption" color="warning.main">
                        {leadsUsage >= 100 ? 'Limite atingido' : 'Próximo do limite'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      Produtos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {products.length} / {productsLimit === Infinity ? '∞' : productsLimit}
                    </Typography>
                  </Box>
                  <StyledLinearProgress 
                    variant="determinate" 
                    value={productsUsage} 
                    color="secondary" 
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight="medium" align="center" gutterBottom>
                      WhatsApp IA Bot
                    </Typography>
                    <Box sx={{ height: 100, position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={whatsappData}
                            cx="50%"
                            cy="50%"
                            innerRadius={25}
                            outerRadius={40}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {whatsappData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          65%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight="medium" align="center" gutterBottom>
                      E-mail Response
                    </Typography>
                    <Box sx={{ height: 100, position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={emailData}
                            cx="50%"
                            cy="50%"
                            innerRadius={25}
                            outerRadius={40}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {emailData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" color="secondary">
                          42%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                color="primary"
                size="small"
                sx={{ 
                  textTransform: 'none',
                  borderRadius: '8px',
                }}
              >
                Ver Detalhes de Uso
              </Button>
            </Box>
          </Box>
          
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Economia e Benefícios
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: 'rgba(63, 81, 181, 0.05)',
                    border: '1px solid rgba(63, 81, 181, 0.1)',
                    height: '100%',
                  }}
                >
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Economia Total
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    R$ 239,40
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Desde o início da sua assinatura
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: 'rgba(245, 0, 87, 0.05)',
                    border: '1px solid rgba(245, 0, 87, 0.1)',
                    height: '100%',
                  }}
                >
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Próxima Renovação
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="secondary.main">
                    25/04/2025
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Plano Premium (Mensal)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HistoryIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.5rem' }} />
              <Typography variant="h6" fontWeight="bold">
                Histórico de Planos
              </Typography>
            </Box>
            
            <Stack spacing={2}>
              {planHistory.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 0.2, minWidth: '90px', py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.date}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Avatar sx={{ bgcolor: item.color, mr: 1 }}>
                      {renderActionIcon(item.action)}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {item.plan}
                    </Typography>
                    <Chip
                      label={renderActionText(item.action)}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: `${item.color}20`,
                        color: item.color,
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: '12px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="body1" fontWeight="medium">
                  Resumo da Assinatura
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Data de início:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  10/01/2025
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Plano atual:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  Premium
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Ciclo de pagamento:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  Mensal
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Valor:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  R$ 79,90/mês
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Próxima cobrança:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  25/04/2025
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                  }}
                >
                  Alterar Plano
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UsageHistory;
